import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ADDR = "https://readdy.ai/api/form/da26jmrk8r5te5mqsih0";

const VALUES = [
  { icon: "ri-compass-3-line", title: "定位", desc: "做韩漫爱好者的一站式追更导航，帮你快速锁定当前最值得追的作品。" },
  { icon: "ri-lightbulb-flash-line", title: "理念", desc: "榜单透明、内容中立，以读者真实人气与口碑为准，不偏袒、不盲从。" },
  { icon: "ri-hand-heart-line", title: "温度", desc: "把复杂的选择变得简单，让每一个追漫的人都能更快找到喜欢的作品。" },
];

const PRINCIPLES = [
  "榜单综合人气、评分与讨论热度动态统计，排名透明可溯。",
  "题材分类与标签持续维护，帮助读者快速筛选心仪方向。",
  "仅提供作品介绍与榜单推荐，内容版权归原作者及版权方所有。",
  "联系方式真实可用，欢迎读者反馈作品信息与追更建议。",
];

const TESTIMONIALS = [
  { name: "追更的小满", text: "之前总在茫茫韩漫里挑花眼，跟着这里的榜单追了《我独自升级》，真的一口气看完，太爽了！" },
  { name: "阿粥", text: "高分榜很靠谱，基本没踩过雷。题材分类也细，恋爱和玄幻都分得很清楚，找作品方便多了。" },
  { name: "林小满", text: "每周来看热度排名，新出的热门基本都能第一时间发现，追更再也不用到处找攻略了。" },
];

export default function About() {
  usePageMeta({
    title: "关于人气韩漫大全｜网站定位、发展理念与联系我们",
    description:
      "了解人气韩漫大全的网站定位、发展理念与内容原则，汇聚韩漫人气榜、高分推荐与题材分类，为你提供可靠的韩漫追更指南，欢迎联系我们反馈建议。",
    path: "/about",
  });

  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honey = (fd.get("website_alt") || "").toString().trim();
    if (honey) {
      form.reset();
      setFormStatus("success");
      setFormMessage("感谢你的反馈，我们会尽快处理。");
      return;
    }
    fd.delete("website_alt");
    const payload = new URLSearchParams(fd as unknown as Record<string, string>);
    setFormStatus("sending");
    setFormError("");
    setFormMessage("");
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        (responseText && responseText !== "OK" ? responseText : "");
      if (res.ok && parsed?.code === "OK") {
        form.reset();
        setFormStatus("success");
        setFormMessage(serverMsg || "感谢你的反馈，我们会尽快处理。");
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络异常，请检查后重试。");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-24 md:px-6 md:py-28">
      <nav className="mb-6 text-xs text-foreground-500" aria-label="面包屑">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
          <li><i className="ri-arrow-right-s-line"></i></li>
          <li aria-current="page" className="text-foreground-700">关于我们</li>
        </ol>
      </nav>

      <div className="max-w-2xl">
        <p className="font-label text-xs font-semibold uppercase tracking-widest text-primary-600">About Us</p>
        <h1 className="mt-1 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于人气韩漫大全</h1>
        <p className="mt-4 text-base leading-relaxed text-foreground-600">
          人气韩漫大全是一份面向韩漫爱好者的追更与榜单资讯平台。我们从读者视角出发，把分散的热门韩漫、
          高分作品与题材分类汇聚在一起，用清晰直观的榜单帮你快速锁定当前最值得追的韩漫，让「想看却不知道看什么」成为过去式。
        </p>
      </div>

      <section className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {VALUES.map((v) => (
          <div key={v.title} className="rounded-xl border border-background-200 bg-background-50 p-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
              <i className={`${v.icon} text-xl`}></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
          </div>
        ))}
      </section>

      <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="rounded-xl border border-background-200 bg-background-100 p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">我们的内容原则</h2>
          <ul className="mt-5 space-y-4">
            {PRINCIPLES.map((p, i) => (
              <li key={p} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-bold text-background-50">
                  {i + 1}
                </span>
                <p className="text-sm leading-relaxed text-foreground-700">{p}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-background-200 bg-background-50 p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">读者口碑</h2>
          <ul className="mt-5 space-y-4">
            {TESTIMONIALS.map((t) => (
              <li key={t.name} className="rounded-lg bg-background-100 p-4">
                <div className="flex items-center gap-1 text-secondary-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">「{t.text}」</p>
                <p className="mt-2 text-xs text-foreground-500">—— {t.name}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground-950">联系我们</h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            如果你有作品信息更正、榜单建议或追更心得想分享，欢迎通过下方表单与我们联系，我们会认真阅读每一封反馈。
          </p>
          <div className="mt-6 space-y-4 text-sm">
            <div className="flex items-center gap-3 text-foreground-700">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                <i className="ri-mail-line"></i>
              </span>
              hello@manhwa-daquan.example
            </div>
            <div className="flex items-center gap-3 text-foreground-700">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                <i className="ri-chat-3-line"></i>
              </span>
              工作日 10:00 - 18:00 在线
            </div>
          </div>
        </div>

        <form id="contact-form" data-readdy-form="联系我们反馈表单" onSubmit={handleSubmit} className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
          <h3 className="font-heading text-lg font-bold text-foreground-950">发送反馈</h3>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-foreground-700">你的称呼</label>
              <input id="cf-name" name="name" type="text" required autoComplete="name" placeholder="怎么称呼你"
                className="h-11 w-full rounded-lg border border-background-200 bg-background-100 px-4 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-300 focus:bg-background-50" />
            </div>
            <div>
              <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-foreground-700">邮箱</label>
              <input id="cf-email" name="email" type="email" required autoComplete="email" placeholder="you@example.com"
                className="h-11 w-full rounded-lg border border-background-200 bg-background-100 px-4 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-300 focus:bg-background-50" />
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="cf-message" className="mb-1.5 block text-sm font-medium text-foreground-700">反馈内容</label>
            <textarea id="cf-message" name="message" required maxLength={500} rows={5} placeholder="写下你的建议、作品信息更正或想说的话"
              className="w-full resize-none rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-300 focus:bg-background-50"></textarea>
            <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
          </div>

          <div className="honeypot-wrap">
            <label htmlFor="cf-website-alt">网站备用字段</label>
            <input id="cf-website-alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>

          {formError && (
            <p className="mt-4 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800" role="alert">
              <i className="ri-error-warning-line mr-1"></i>
              {formError}
            </p>
          )}
          {formStatus === "success" && (
            <p className="mt-4 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-900" role="status">
              <i className="ri-checkbox-circle-line mr-1"></i>
              {formMessage}
            </p>
          )}
          <button
            type="submit"
            disabled={formStatus === "sending"}
            className="mt-5 w-full whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 sm:w-auto"
          >
            {formStatus === "sending" ? "提交中…" : "提交反馈"}
          </button>
        </form>
      </section>
    </div>
  );
}